import { BasicMonitorStatus, BarInterval, ORDER_SIDE } from '@/services/enums';
import React, { useState, useEffect, useRef } from 'react';
import { MonitorSignalRule } from '../services/klineTypes';
import { klineDataService } from '../services/klineDataService';
import { klineMonitorService, MonitorTask } from '../services/klineMonitorService';
import { useToastStore } from '../stores/useToastStore';
import MultiSelectDropdown from './MultiSelectDropdown';
import SignalList from './kline-monitor/SignalList';
import MonitorStatusPanel from './kline-monitor/MonitorStatusPanel';
import StopLossPanel from './kline-monitor/StopLossPanel';
import MonitorControlPanel from './kline-monitor/MonitorControlPanel';
import EditMonitorRuleDialog from './kline-monitor/EditMonitorRuleDialog';

import { fixedRuleOptions } from './kline-monitor/constants';
import { useKlineMonitorStore } from '../stores/useKlineMonitorStore';

interface KlineAIMonitorDialogProps {
  symbol: string;
  bar: BarInterval;
  enableAutoTrading?: boolean; // 是否启用自动交易（从父组件传递）
  onClose: (stateChange?: { status: BasicMonitorStatus; monitorId?: string }) => void;
}

const KlineAIMonitorDialog: React.FC<KlineAIMonitorDialogProps> = ({
  symbol,
  bar,
  enableAutoTrading: initialEnableAutoTrading = false,
  onClose,
}) => {
  const showToast = useToastStore((state) => state.show);
  const { getMonitorStatus } = useKlineMonitorStore();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_monitorId, setMonitorId] = useState<string | null>(null); // monitorId 通过 monitorIdRef.current 使用
  const [monitoring, setMonitoring] = useState(false);
  const [monitorData, setMonitorData] = useState<any>(null);
  const [loading, setLoading] = useState(true); // 添加加载状态
  const [toggleLoading, setToggleLoading] = useState(false); // 开始/停止监控的加载状态
  const [enableAutoTrading, setEnableAutoTrading] = useState(initialEnableAutoTrading); // 是否启用自动交易
  const [fixedRules, setFixedRules] = useState<string[]>([]); // 固定规则列表
  const [previousTriggeredSignals, setPreviousTriggeredSignals] = useState<Set<string>>(new Set());
  const [showEditDialog, setShowEditDialog] = useState(false); // 编辑对话框显示状态

  // 根据选中的固定规则ID，获取完整的信号列表
  const getFixedRuleSignals = () => {
    return fixedRuleOptions.filter((option) => fixedRules.includes(option.id)).map((option) => option.signal);
  };
  const triggeredAtSetRef = useRef<Set<string>>(new Set()); // 用于跟踪已提示过的triggeredAt，确保相同截止时间的信号只提示一次
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const monitorIdRef = useRef<string | null>(null);
  const shakeRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<boolean>(false); // 用于防止重复请求
  const mountedRef = useRef<boolean>(true); // 跟踪组件是否已挂载

  // 打开对话框时，先检查是否有正在运行的监控任务，如果没有则获取缓存的监控规则

  useEffect(() => {
    mountedRef.current = true; // 确保组件挂载状态为true

    // 使用父组件传递的 enableAutoTrading 作为初始状态
    setEnableAutoTrading(initialEnableAutoTrading);

    // 重置加载状态
    loadingRef.current = true;
    setLoading(true);

    const checkExistingTask = async () => {
      console.log(`[KlineAIMonitorDialog] Checking existing task for ${symbol} ${bar}`);
      try {
        if (!mountedRef.current) return;

        // 设置超时保护，防止无限等待
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000),
        );

        // 使用 Promise.race 添加超时控制
        const task = (await Promise.race([
          klineMonitorService.findRunningMonitor(symbol, bar),
          timeoutPromise,
        ])) as MonitorTask | null;

        console.log(`[KlineAIMonitorDialog] Task found:`, task ? 'Yes' : 'No');

        if (!mountedRef.current) return;

        if (task) {
          monitorIdRef.current = task.monitorId;
          setMonitorId(task.monitorId);
          setMonitoring(true);
          setMonitorData(task);
          // 如果有正在运行的任务，同步自动交易状态
          if (task.enableAutoTrading !== undefined) {
            setEnableAutoTrading(task.enableAutoTrading);
          }
          // 如果有正在运行的任务，同步固定规则
          if (task.fixedRules && task.fixedRules.length > 0) {
            try {
              const ruleIds = task.fixedRules
                .map((rule: MonitorSignalRule) => {
                  // 根据规则内容尝试匹配 ID
                  const matchedOption = fixedRuleOptions.find((opt) => {
                    const s = opt.signal;
                    return (
                      s.type === rule.signalType &&
                      s.conditions[0]?.field === rule.conditions[0]?.field &&
                      s.conditions[0]?.operator === rule.conditions[0]?.operator &&
                      s.conditions[0]?.value === rule.conditions[0]?.value
                    );
                  });
                  return matchedOption?.id;
                })
                .filter((id: string | undefined): id is string => id !== undefined);
              setFixedRules(ruleIds);
            } catch (err) {
              console.error('Error processing fixed rules:', err);
            }
          }
          startPolling(task.monitorId);
        } else {
          // 如果没有正在运行的任务，获取缓存的监控规则
          console.log(`[KlineAIMonitorDialog] No running task, fetching cached AI response...`);
          try {
            // 同样添加超时保护
            const cachedResponse = (await Promise.race([
              klineDataService.getAIResponse(symbol, bar),
              timeoutPromise,
            ])) as any;

            console.log(`[KlineAIMonitorDialog] Cached response found:`, cachedResponse ? 'Yes' : 'No');

            if (mountedRef.current && cachedResponse) {
              setMonitorData({
                aiResponse: cachedResponse,
                monitorRules: cachedResponse.monitorRules,
              });
            }
          } catch (error) {
            console.error('Failed to get cached AI response:', error);
          }
        }
      } catch (error) {
        console.error('Failed to check existing task:', error);
      } finally {
        if (mountedRef.current) {
          console.log(`[KlineAIMonitorDialog] Loading finished`);
          loadingRef.current = false;
          setLoading(false); // 隐藏加载状态
        }
      }
    };

    checkExistingTask();

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, bar]); // 依赖项包含 symbol 和 bar，当它们变化时重新执行

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, []);

  const startPolling = (id: string) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(async () => {
      try {
        if (!mountedRef.current) return;
        const status = await getMonitorStatus(id);
        if (!mountedRef.current) return;

        if (status) {
          setMonitorData(status);

          // 检查是否有新触发的信号
          const newTriggeredSignals = new Set<string>();
          let hasNewTrigger = false;

          const checkSignal = (signal: any, index: number, isSell: boolean) => {
            if (signal.triggered) {
              const signalId = `${isSell ? ORDER_SIDE.SELL : ORDER_SIDE.BUY}-${index}-${signal.triggeredAt || Date.now()}`;
              newTriggeredSignals.add(signalId);

              // 如果是新触发的信号（不在上次的集合中），且触发时间在最近10秒内
              if (!previousTriggeredSignals.has(signalId)) {
                // 检查触发时间
                const triggerTime = signal.triggeredAt ? new Date(signal.triggeredAt).getTime() : 0;
                const now = Date.now();
                // 只有最近10秒内触发的信号才提示，避免重复提示旧信号
                // 另外，如果是刚刚加载的任务，previousTriggeredSignals 为空，也不应该提示太旧的信号
                if (now - triggerTime < 10000) {
                  // 检查是否已经提示过这个 triggeredAt
                  // 使用 signal.triggeredAt + signal type + index 作为唯一标识
                  const uniqueKey = `${signal.triggeredAt}-${isSell ? ORDER_SIDE.SELL : ORDER_SIDE.BUY}-${index}`;
                  if (signal.triggeredAt && !triggeredAtSetRef.current.has(uniqueKey)) {
                    hasNewTrigger = true;
                    triggeredAtSetRef.current.add(uniqueKey);
                  }
                }
              }
            }
          };

          if (status.signals) {
            status.signals.forEach((s: any, i: number) => checkSignal(s, i, s.type === ORDER_SIDE.SELL));
          }

          setPreviousTriggeredSignals(newTriggeredSignals);

          if (hasNewTrigger) {
            // 播放提示音或震动
            if (navigator.vibrate) navigator.vibrate([200, 100, 200]);

            // 窗口抖动效果
            if (shakeRef.current) {
              shakeRef.current.classList.add('animate-shake');
              setTimeout(() => {
                if (shakeRef.current) {
                  shakeRef.current.classList.remove('animate-shake');
                }
              }, 500);
            }
          }
        } else {
          // 任务可能已停止
          setMonitoring(false);
          setMonitorId(null);
          monitorIdRef.current = null;
          stopPolling();
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const toggleMonitoring = async () => {
    if (toggleLoading) return;

    setToggleLoading(true);
    try {
      if (monitoring) {
        if (monitorIdRef.current) {
          await klineMonitorService.stopMonitor(monitorIdRef.current);
          setMonitoring(false);
          setMonitorId(null);
          const stoppedId = monitorIdRef.current;
          monitorIdRef.current = null;
          stopPolling();
          onClose({ status: 'stopped', monitorId: stoppedId });
          showToast('监控已停止', 'success');
        }
      } else {
        // 获取当前选中的固定规则
        const currentFixedRules = getFixedRuleSignals();
        // 传递 enableAutoTrading 状态
        // 注意：这里使用的是 enableAutoTrading 的当前状态
        const autoTrading = enableAutoTrading;

        const task = await klineMonitorService.startMonitor(
          symbol,
          bar,
          autoTrading,
          currentFixedRules.length > 0 ? currentFixedRules : undefined,
        );

        monitorIdRef.current = task.monitorId;
        setMonitorId(task.monitorId);
        setMonitoring(true);
        setMonitorData(task);
        startPolling(task.monitorId);
        // 通知父组件监控已开始，传递 monitorId
        onClose({ status: 'running', monitorId: task.monitorId });
        showToast(`监控已启动${autoTrading ? ' (自动交易已开启)' : ''}`, 'success');
      }
    } catch (error: any) {
      showToast(error.message || '操作失败', 'error');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleEditClick = () => {
    setShowEditDialog(true);
  };

  const handleEditSuccess = async () => {
    // 如果没有在监控中，刷新数据以显示最新保存的内容
    if (!monitoring) {
      setLoading(true);
      try {
        const cachedResponse = await klineDataService.getAIResponse(symbol, bar);
        if (cachedResponse) {
          setMonitorData({
            aiResponse: cachedResponse,
            monitorRules: cachedResponse.monitorRules,
          });
        }
      } catch (error) {
        console.error('Failed to refresh data after edit:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={shakeRef}
        className="bg-gray-900 border border-white/20 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold text-white">AI 智能监控</h3>
            <div className="px-2 py-1 rounded bg-white/5 border border-white/10 text-xs font-mono text-cyan-400">
              {symbol} · {bar}
            </div>
          </div>
          <button onClick={() => onClose()} className="text-white/60 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <svg
              className="animate-spin h-8 w-8 text-cyan-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        ) : (
          <>
            <MonitorStatusPanel monitorData={monitorData} />

            <StopLossPanel
              suggestion={monitorData?.positionSuggestion || monitorData?.monitorRules?.positionSuggestion}
            />

            {!monitoring && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                <div className="text-sm font-semibold text-white mb-2">固定监控规则</div>
                <MultiSelectDropdown
                  options={fixedRuleOptions.map((o) => o.id)}
                  value={fixedRules}
                  onChange={setFixedRules}
                  placeholder="选择固定监控规则..."
                  getOptionLabel={(id) => fixedRuleOptions.find((o) => o.id === id)?.label || id}
                />
              </div>
            )}

            <MonitorControlPanel
              monitoring={monitoring}
              toggleLoading={toggleLoading}
              toggleMonitoring={toggleMonitoring}
              enableAutoTrading={enableAutoTrading}
              setEnableAutoTrading={setEnableAutoTrading}
              monitorData={monitorData}
            />

            <SignalList symbol={symbol} monitorData={monitorData} onEditClick={handleEditClick} />
          </>
        )}
      </div>

      <EditMonitorRuleDialog
        isOpen={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        symbol={symbol}
        bar={bar}
        monitorData={monitorData}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default KlineAIMonitorDialog;
